import { type CurrentUser } from '@repo/api-client';
import { create } from 'zustand';

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous';

interface SessionState {
  /** `loading` until startup restoration has settled. */
  status: SessionStatus;
  /**
   * Access token — memory only, never persisted.
   *
   * Persisting it would expose it to XSS for its whole lifetime; it is
   * short-lived (15 min) and regenerates from the stored refresh token instead.
   */
  accessToken: string | null;
  user: CurrentUser | null;

  authenticate: (accessToken: string, user: CurrentUser) => void;
  /** After a refresh: only the access token changes. */
  renewAccessToken: (accessToken: string) => void;
  updateUser: (user: CurrentUser) => void;
  reset: () => void;
}

export const useSessionStore = create<SessionState>((set) => ({
  status: 'loading',
  accessToken: null,
  user: null,

  authenticate: (accessToken, user) =>
    set({ status: 'authenticated', accessToken, user }),

  renewAccessToken: (accessToken) => set({ accessToken }),

  updateUser: (user) => set({ user }),

  reset: () => set({ status: 'anonymous', accessToken: null, user: null }),
}));

/**
 * Reads the token outside React.
 *
 * `lib/http.ts` needs it while not being a component — the reason the
 * session lives in a Zustand store rather than a React context.
 */
export const readAccessToken = (): string | null =>
  useSessionStore.getState().accessToken;
